import React, { useEffect, useState } from 'react';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { useInterval } from '@jbhi-fi/utils/src/useInterval/useInterval';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { styles } from './GoogleRelated.css';
import { AlgoliaProductHit } from '../../../../utils/types';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { SmartModuleSettings } from '../../../../../global';
import { ProductSuggestionsContainer } from '@jbhi-fi/components/src/ProductSuggestionsContainer';

const hideUnavailableProducts = (item: AlgoliaProductHit) =>
  item.product_published && item.price > 0 && item.availability.displayProduct && item.availability.canBuyOnline;

type GoogleRelatedProps = {
  data?: Array<AlgoliaProductHit>;
  title: string;
  type: 'frequentlyBoughtTogether' | 'recommendedForYou';
  page: string;
  location: string;
  settings?: SmartModuleSettings;
  buttonType: 'primary' | 'secondary';
  variant?: 'carousel' | 'list';
  isEnhancedGoogleRecommendationsEnabled?: boolean;
};

const GoogleRelated = ({
  data,
  title,
  type,
  page,
  location,
  settings,
  buttonType,
  variant = 'carousel',
  isEnhancedGoogleRecommendationsEnabled,
}: GoogleRelatedProps): JSX.Element => {
  const [shouldShowRelated, setShouldShowRelated] = useState(settings?.isEnabled);
  const hasLoaded = data?.length;
  const googleRecModelType = settings?.googleRecModelType || 'OYML';
  const trackingListName = `recommendations-${page}`;
  const ecommerceExtraProps = {
    list: title,
    itemListSlot: location,
    itemListServiceConfig: googleRecModelType,
  };
  const trackingDescriptor = {
    page,
    group: 'googleRelated',
    category: type,
    ecommerceExtraProps: ecommerceExtraProps,
  };
  const imsSKUs = document.querySelector('.pdp-related-skus')?.getAttribute('data-related-skus')?.split(',');
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const isAlternativesEnabled = settings?.isAlternativesEnabled && isEnhancedGoogleRecommendationsEnabled;

  const { handleViewAlternatives } = useAlternativesContext();

  const compareProps =
    type !== 'frequentlyBoughtTogether'
      ? {
          onCompare: compareHandleClick,
          getShouldShowCompare: shouldShowCompare,
          getIsCompared: isAddedToCompare,
        }
      : {};

  const wishlistProps = !isEnhancedGoogleRecommendationsEnabled
    ? {
        isLoggedIn,
        onUpdateWishlist: handleUpdateWishlist,
        getIsInWishlist,
      }
    : {};

  const handleLoadingTimeout = () => {
    if (!hasLoaded) {
      setShouldShowRelated(false);
    }
  };

  useInterval(handleLoadingTimeout, window.smartModuleSettings?.loadingTimeout || 5000, true);

  const addTrackingFBTSource = (sku: string) => {
    if (type === 'frequentlyBoughtTogether') {
      const customIndex = settings?.googleRecModelType ?? googleRecModelType;
      const FBTSource = imsSKUs?.includes(sku) ? 'ims' : 'recai';
      const indexName = customIndex ? `${customIndex.toLowerCase()}-${FBTSource}` : FBTSource;
      return `${trackingListName}-${indexName}`;
    }
    return trackingListName;
  };

  let filteredData = data?.filter(hideUnavailableProducts).slice(0, settings?.productCount);

  if (filteredData?.length && Array.isArray(imsSKUs) && imsSKUs.length > 0) {
    const matchedProducts = filteredData.filter(p => imsSKUs.includes(p.sku));

    if (matchedProducts.length > 0) {
      const sortedMatchedProducts = matchedProducts.sort((a, b) => imsSKUs.indexOf(a.sku) - imsSKUs.indexOf(b.sku));
      filteredData = sortedMatchedProducts.concat(filteredData.filter(p => !imsSKUs.includes(p.sku)));
    }
  }

  useEffect(() => {
    const productImpressions = filteredData?.map(item => {
      const product = item.google_tag_manager;
      product.recommendationSource = addTrackingFBTSource(item.sku);
      return product;
    });

    if (productImpressions?.length) {
      window.gtm.track('productImpression', {
        products: productImpressions,
      });
    }
  }, [data, type, trackingListName]);

  const handleAddToCart = async (e: React.SyntheticEvent, hit: AlgoliaProductHit) => {
    if (!onAddToCart) return false;
    const isSuccess = await onAddToCart(e, hit, hit.variant_id, trackingDescriptor);
    return isSuccess;
  };

  return (
    <>
      {shouldShowRelated && filteredData?.length ? (
        <div data-testid={`${page}-google-related-products-container`}>
          {location === 'cart' ? (
            <h3 className={styles.cartHeading}>{title}</h3>
          ) : (
            <CalloutFont as="h3" className={styles.heading} testId={`${page}-google-related-products-title`}>
              {title}
            </CalloutFont>
          )}

          {isEnhancedGoogleRecommendationsEnabled && variant === 'list' ? (
            <ProductSuggestionsContainer
              tileVariant="minified"
              suggestions={filteredData}
              onAddToCart={handleAddToCart}
              type={type}
              trackingProps={ecommerceExtraProps}
            />
          ) : (
            <ProductCarousel
              className={styles.carousel}
              fullBleed={false}
              fullHeightItems
              trackingDescriptor={trackingDescriptor}
              products={filteredData}
              productCardSkeletonProps={{ variant: 'tablet' }}
              renderProduct={({ hit, trackingDescriptor }) => (
                <ProductCard
                  {...AlgoliaHitTransformer.toProductCardProps(hit, { trackingDescriptor })}
                  shouldHideWishlist={isEnhancedGoogleRecommendationsEnabled}
                  {...compareProps}
                  {...wishlistProps}
                  {...productCartPropsOverrides}
                  variant={isEnhancedGoogleRecommendationsEnabled ? 'minified' : 'tablet'}
                  actionButtonType={buttonType}
                  tooltipPosition="top"
                  onAddToCart={
                    onAddToCart
                      ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                      : undefined
                  }
                  onViewAlternatives={
                    isAlternativesEnabled
                      ? () =>
                          handleViewAlternatives(
                            hit,
                            settings.alternativesCount ?? 4,
                            'alternatives',
                            ecommerceExtraProps
                          )
                      : undefined
                  }
                />
              )}
            />
          )}
        </div>
      ) : null}
    </>
  );
};

export default GoogleRelated;
